import React from 'react';
import { render, unmountComponentAtNode } from 'react-dom';
import { CurrentUser } from 'Roblox';
import '../../../css/accountSecurityPrompt/accountSecurityPrompt.scss';
import '../../../css/accountSecurityPrompt/banner.scss';
import '../../../css/accountSecurityPrompt/modalModern.scss';
import App from './App';
import { LOG_PREFIX, ROOT_ELEMENT_ID } from './app.config';
import { DismissOption, PromptType, ViewType } from './constants/parameters';
import { AccountSecurityError } from './constants/requests';
import { EventServiceDefault } from './services/eventService';
import { PreferenceServiceDefault } from './services/preferenceService';
import { RequestServiceDefault } from './services/requestService';

// Global instance since we do not need prompt parameters for instantiation.
const requestServiceDefault = new RequestServiceDefault();

/**
 * Renders the account security prompt for a given set of parameters.
 */
const renderApp = (
  promptType: PromptType,
  viewType: ViewType,
  dismissOption: DismissOption,
  isUserUnder13: boolean
) => {
  const rootElement = document.getElementById(ROOT_ELEMENT_ID);
  if (rootElement !== null) {
    // Remove any existing instances of the app.
    unmountComponentAtNode(rootElement);

    // Instantiate services externally to the app, which will offer future
    // flexibility (e.g. for mocking).
    const eventService = new EventServiceDefault(promptType, viewType, dismissOption);
    const preferenceService = new PreferenceServiceDefault(
      CurrentUser.userId,
      promptType,
      viewType
    );

    // Render the app on the selected element.
    render(
      <App
        promptType={promptType}
        viewType={viewType}
        dismissOption={dismissOption}
        isUserUnder13={isUserUnder13}
        eventService={eventService}
        preferenceService={preferenceService}
        requestService={requestServiceDefault}
      />,
      rootElement
    );
  }
};

/**
 * Retrieves parameters for and renders an account security prompt (based on
 * the currently authenticated user).
 */
const retrieveAndRenderPrompt = async (): Promise<void> => {
  const result = await requestServiceDefault.getSecurityPromptParameters();
  if (result.isError) {
    // eslint-disable-next-line no-console
    console.warn(
      LOG_PREFIX,
      'Retrieving prompt parameters failed with error',
      result.error && AccountSecurityError[result.error]
    );
    return;
  }

  const parameters = result.value;
  if (parameters.promptType === null) {
    // eslint-disable-next-line no-console
    console.log(LOG_PREFIX, 'No parameters were retrieved');
    return;
  }
  const { promptType, viewType, dismissOption } = parameters;
  renderApp(promptType, viewType, dismissOption, CurrentUser.isUnder13);
};

// Sanity-check that the current user is authenticated (even to render
// prompts manually via the JavaScript console).
if (CurrentUser && CurrentUser.isAuthenticated) {
  // Add some variables to the global `Roblox` object for manual testing via the
  // JavaScript console.
  Object.assign(Roblox, {
    AccountSecurityPrompt: {
      render: renderApp,
      clearPreferences: () => PreferenceServiceDefault.clear(),
      PromptType,
      ViewType,
      DismissOption
    }
  });
  // eslint-disable-next-line no-void
  void retrieveAndRenderPrompt();
}
